/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { RootState } from "@store/store";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { useLazyGetClientsSuppliersByRfcQuery } from "@api/proveedorClienteApi";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { useLazyGetReferencesQuery, usePatchReferencesMutation, usePostReferencesMutation } from "@api/referenciasApi";
import { useLazyGetReferenceTypesQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { usePutApplicationCompletedMutation } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import {
  createEmptyReference,
  getFormikArrayError,
  getNumberOrDefault,
  getOrdinalText,
  getReferenceIndex,
} from "@helpers/funciones";
import { REFERENCE_DEAFUALT_VALUES, REFERENCES_CODES } from "@helpers/constantes";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, ClientSupplier, ReferenceType, ReferenceSupplierError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";

const OTRO_PROVEEDOR_NAME = "OTRO";

const ReferenciasProveedores = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const client = useSelector((state: RootState) => state.register.personal_data);
  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);

  const [triggerPostReferences] = usePostReferencesMutation();
  const [triggerPatchReferences] = usePatchReferencesMutation();
  const [getClientsSuppliersByRFC] = useLazyGetClientsSuppliersByRfcQuery();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [getReferenceTypes] = useLazyGetReferenceTypesQuery();
  const [getReferences] = useLazyGetReferencesQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [referenceType, setReferenceType] = useState<ReferenceType | null>(null);
  const [supplierList, setSupplierList] = useState<ClientSupplier[]>([]);
  const [minReference, setMinReference] = useState(REFERENCE_DEAFUALT_VALUES.min);
  const [requiredReference, setRequiredReference] = useState(REFERENCE_DEAFUALT_VALUES.required);

  const pendingReferenceSaves = useRef<Record<number, Promise<void> | undefined>>({});

  const formik = useFormik({
    initialValues: {
      referencia: Array.from({ length: REFERENCE_DEAFUALT_VALUES.required }, () => createEmptyReference("proveedor")),
    },
    validationSchema: yup.object().shape({
      referencia: yup.array().of(
        yup.object().shape({
          proveedor: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          country_code: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          phone: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(PHONE_NUMBER, { message: "Número de teléfono inválido", excludeEmptyString: true })
            .test("unique", "El teléfono ya se encuentra registrado.", function (value, params) {
              if (!value) return true;
              const reference_index = getReferenceIndex(params.path);
              const reference_phone =
                (formik.values.referencia[reference_index].country_code || "") +
                (formik.values.referencia[reference_index].phone || "") +
                (formik.values.referencia[reference_index].phone_extension || "");
              const array_items = formik.values.referencia.map((item) => {
                return (item.country_code || "") + (item.phone || "") + (item.phone_extension || "");
              });

              const num_results: number = array_items.filter((item) => item === reference_phone).length;
              return num_results > 1 ? false : true;
            }),
          contact_name: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .test("unique", "El nombre de contacto ya se encuentra registrado.", function (value) {
              if (!value) return true;
              const array_items = formik.values.referencia.map((item) =>
                (item.contact_name || "").replaceAll(" ", "").toLowerCase(),
              );
              const name_to_compare = value.replaceAll(" ", "").toLowerCase();
              const num_results: number = array_items.filter((item) => item === name_to_compare).length;
              return num_results > 1 ? false : true;
            }),
          email: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(EMAIL, {
              message: "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)",
              excludeEmptyString: true,
            })
            .test("unique", "El email ya se encuentra registrado.", function (value) {
              if (!value) return true;
              const array_items = formik.values.referencia.map((item) => item.email || "");
              const num_results: number = array_items.filter((item) => item === value).length;
              return num_results > 1 ? false : true;
            }),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid || !referenceType) {
          return;
        }

        setSubmitting(true);

        for (let index = 0; index < values.referencia.length; index++) {
          if (isReferenceEmpty(values.referencia[index])) continue;
          await acquireAndPersist(index);
        }

        await putAapplicationCompleted({
          id_sol: applicationData.id,
          body: {
            references_status: "COMPLETO",
          },
        }).unwrap();

        navigate("/producto/creditosimple/avance_expediente_digital");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasProveedores-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isReferenceEmpty = (reference: (typeof formik.values.referencia)[number]) =>
    !reference.proveedor && !reference.contact_name && !reference.email && !reference.phone;

  const persistReference = async (index: number) => {
    const reference = formik.values.referencia[index];
    const supplierItem = supplierList.find((supplier) => supplier.rfc === reference.proveedor);

    const referenceData = {
      client_details_id: client.id,
      id_ext: "4444",
      phone_number: reference.phone,
      reference_type_id: referenceType?.id,
      code_phone_number: reference.country_code,
      phone_extension: reference.phone_extension,
      supplier_name: supplierItem?.name,
      supplier_rfc: supplierItem?.rfc,
      contact_name: reference.contact_name,
      email: reference.email,
      percentage_share: Number(supplierItem?.share),
    };

    if (reference.id) {
      await triggerPatchReferences({
        referenceId: reference.id as UUID,
        body: referenceData,
      }).unwrap();
    } else {
      const newReference = await triggerPostReferences(referenceData).unwrap();
      formik.setFieldValue(`referencia[${index}].id`, newReference.data.id);
    }
  };

  // Ensures at most one save request is ever in-flight per row, so a blur-triggered
  // save racing with the submit save can't create two backend records for the same row.
  const acquireAndPersist = async (index: number) => {
    if (pendingReferenceSaves.current[index]) {
      await pendingReferenceSaves.current[index];
      return;
    }

    const savePromise = persistReference(index);
    pendingReferenceSaves.current[index] = savePromise;
    try {
      await savePromise;
    } finally {
      delete pendingReferenceSaves.current[index];
    }
  };

  const saveReference = async (
    e:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | Element>
      | React.FocusEvent<HTMLInputElement, Element>
      | React.SyntheticEvent<Element, Event>,
    index: number,
  ) => {
    formik.handleBlur(e);

    if (!referenceType) return;
    if (isReferenceEmpty(formik.values.referencia[index])) return;

    if (formik.errors.referencia?.[index]) {
      const {
        proveedor: proveedor_err,
        contact_name: contact_name_err,
        email: email_err,
        country_code: country_code_err,
        phone: phone_err,
        phone_extension: phone_extension_err,
      } = formik.errors?.referencia?.[index] as unknown as ReferenceSupplierError;

      if (proveedor_err || contact_name_err || email_err || country_code_err || phone_err || phone_extension_err) {
        return;
      }
    }

    try {
      await acquireAndPersist(index);
    } catch (error) {
      console.error("Error guardando referencia:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!client.id) return;
        const [reference_types, client_suppliers, tax_systems] = await Promise.all([
          getReferenceTypes().unwrap(),
          getClientsSuppliersByRFC(String(company?.rfc)).unwrap(),
          getTaxSystems(application.type).unwrap(),
        ]);

        const references_config = tax_systems?.config_extra?.references_config?.[REFERENCES_CODES.proveedor];

        const required = getNumberOrDefault(references_config?.required, REFERENCE_DEAFUALT_VALUES.required);
        const min = getNumberOrDefault(references_config?.min, REFERENCE_DEAFUALT_VALUES.min);

        setRequiredReference(required);
        setMinReference(min);

        const reference_type_proveedor = reference_types.find((item) => item.code === REFERENCES_CODES.proveedor);
        if (!reference_type_proveedor) return;

        setSupplierList(client_suppliers.suppliers);
        setReferenceType(reference_type_proveedor);

        const references_result = await getReferences({
          clientDetailsId: client.id,
          referenceTypeId: reference_type_proveedor.id,
        }).unwrap();

        const values = references_result
          .filter((item) => item.reference_type_id === reference_type_proveedor.id)
          .slice(0, required)
          .map((item) => {
            return {
              id: item.id,
              country_code: item.code_phone_number,
              phone: item.phone_number,
              contact_name: item.contact_name,
              proveedor: item.supplier_rfc,
              email: item.email,
              phone_extension: item.phone_extension,
              share: item.share,
            };
          });

        for (let index = values.length; index < required; index++) {
          values.push(createEmptyReference("proveedor"));
        }

        formik.setFieldValue("referencia", values);

        setTimeout(() => {
          formik.validateForm();
        }, 200);

        window.scrollTo(0, 0);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasProveedores-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Referencias
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>2</strong> de 2
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Ahora, escribe {requiredReference} referencias que sean tus proveedores, considera que podríamos
            contactarlos. Debes completar al menos {minReference} para continuar.
          </Typography>

          <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              {formik.values.referencia.map((_, index) => (
                <div key={index}>
                  <Box mt={4} mb={2}>
                    <Typography
                      textAlign={"left"}
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={400}
                      color={theme?.palette?.primary?.dark}
                    >
                      {getOrdinalText(index + 1)} referencia
                    </Typography>
                  </Box>
                  <FormControl
                    fullWidth
                    required={index < minReference}
                    sx={{
                      ...customStylesAsterisk,
                      mt: 1,
                    }}
                    error={Boolean(getFormikArrayError(formik, "referencia", index, "proveedor"))}
                  >
                    <InputLabel id={`${index}proveedor`}>Proveedores</InputLabel>
                    <Select
                      labelId="proveedor"
                      name={`referencia[${index}].proveedor`}
                      id={`proveedor${index}`}
                      fullWidth
                      required={index < minReference}
                      label="Proveedores"
                      value={formik.values.referencia?.[index]?.proveedor}
                      onBlur={(e) => saveReference(e, index)}
                      onChange={formik.handleChange}
                      error={Boolean(getFormikArrayError(formik, "referencia", index, "proveedor"))}
                      disabled={formik.isSubmitting || isLoading}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {supplierList
                        ?.filter(
                          (supplier) =>
                            supplier.name === OTRO_PROVEEDOR_NAME ||
                            !formik.values.referencia.some(
                              (ref, refIndex) => refIndex !== index && ref.proveedor === supplier.rfc,
                            ),
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((supplier) => (
                          <MenuItem key={supplier.rfc} value={supplier.rfc}>
                            {supplier.name.toUpperCase()}
                          </MenuItem>
                        ))}
                    </Select>

                    {Boolean(getFormikArrayError(formik, "referencia", index, "proveedor")) && (
                      <FormHelperText>{getFormikArrayError(formik, "referencia", index, "proveedor")}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      name={`referencia[${index}].contact_name`}
                      id={`contact_name${index}`}
                      label="Nombre completo del contacto"
                      margin="normal"
                      required={index < minReference}
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={(e) => saveReference(e, index)}
                      error={Boolean(getFormikArrayError(formik, "referencia", index, "contact_name"))}
                      helperText={getFormikArrayError(formik, "referencia", index, "contact_name")}
                      value={formik.values.referencia?.[index]?.contact_name}
                      disabled={formik.isSubmitting || isLoading}
                    />
                  </FormControl>

                  <TextField
                    name={`referencia[${index}].email`}
                    id={`email${index}`}
                    label="Correo electrónico"
                    margin="normal"
                    required={index < minReference}
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      const value_to_set = (e.target.value || "").toLowerCase();
                      formik.setFieldValue(`referencia[${index}].email`, value_to_set);
                    }}
                    onBlur={(e) => saveReference(e, index)}
                    error={Boolean(getFormikArrayError(formik, "referencia", index, "email"))}
                    helperText={getFormikArrayError(formik, "referencia", index, "email")}
                    value={formik.values.referencia?.[index]?.email}
                    disabled={formik.isSubmitting}
                    inputProps={{ maxLength: 100 }}
                  />

                  <Grid container spacing={1} mt={1}>
                    <Grid item xs={4} sm={4} md={4}>
                      <CustomContryCode
                        label="Código"
                        name={`referencia[${index}].country_code`}
                        value={formik.values.referencia?.[index]?.country_code}
                        onChange={formik.handleChange}
                        onBlur={(e) => saveReference(e, index)}
                        error={Boolean(getFormikArrayError(formik, "referencia", index, "country_code"))}
                        disabled={formik.isSubmitting || isLoading}
                      />
                    </Grid>

                    <Grid item xs={8} sm={8} md={8}>
                      <TextField
                        name={`referencia[${index}].phone`}
                        id={`phone${index}`}
                        type="text"
                        variant="outlined"
                        label="Teléfono"
                        fullWidth
                        required={index < minReference}
                        value={formik.values.referencia?.[index]?.phone}
                        onChange={(event) => {
                          formik.setFieldValue(`referencia[${index}].phone`, event.target.value.replace(/[^0-9]/g, ""));
                        }}
                        onBlur={(e) => saveReference(e, index)}
                        error={Boolean(getFormikArrayError(formik, "referencia", index, "phone"))}
                        helperText={getFormikArrayError(formik, "referencia", index, "phone")}
                        inputProps={{
                          maxLength: 10,
                          inputMode: "numeric",
                        }}
                        disabled={formik.isSubmitting || isLoading}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    name={`referencia[${index}].phone_extension`}
                    id={`phone_extension${index}`}
                    label="Extensión del teléfono(si aplica)"
                    margin="normal"
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      const value_to_set = (e.target.value || "").replace(/[^0-9]/g, "");
                      formik.setFieldValue(`referencia[${index}].phone_extension`, value_to_set);
                    }}
                    onBlur={(e) => saveReference(e, index)}
                    error={Boolean(getFormikArrayError(formik, "referencia", index, "phone_extension"))}
                    helperText={getFormikArrayError(formik, "referencia", index, "phone_extension")}
                    value={formik.values.referencia?.[index]?.phone_extension}
                    disabled={formik.isSubmitting}
                    inputProps={{
                      maxLength: 5,
                      inputMode: "numeric",
                    }}
                  />
                </div>
              ))}
              <Grid container spacing={1} my={2}>
                <Grid item xs={6} sm={6} md={6}>
                  <Button
                    variant="blue_outlined"
                    type="button"
                    fullWidth
                    sx={{ padding: "0px", height: "100%" }}
                    onClick={() => {
                      navigate("/producto/creditosimple/avance_expediente_digital");
                    }}
                    disabled={formik.isSubmitting || isLoading}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>

                <Grid item xs={6} sm={6} md={6}>
                  <Button
                    variant="default"
                    type="submit"
                    fullWidth
                    disabled={!formik.isValid || formik.isSubmitting || isLoading}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ReferenciasProveedores;
